/**
 * entitlement.service.ts — what the signed-in user's plan unlocks.
 *
 * Raw snake_case, matching the backend EntitlementsDTO exactly (no camelCase transform).
 */

import { apiClient } from './client';
import { handleAPIError } from './error-handler';

export type EntitlementSource = 'school' | 'subscription' | 'trial' | 'free' | 'staff';

export interface UpgradeHint {
  plan_code: string;
  plan_name: string;
  plan_group?: string | null;
}

export interface Entitlements {
  plan_code: string | null;
  plan_name: string;
  source: EntitlementSource;
  trial_ends_at: string | null;
  school_id: string | null;
  school_name: string | null;
  enforcement: 'off' | 'soft' | 'enforce';
  /** feature_key -> per-plan config ({} when the plan has none, e.g. {"max_attempts": 3}) */
  features: Record<string, Record<string, any>>;
  upgrade_hints: Record<string, UpgradeHint>;
  /** Announced features nobody (except staff) can use yet */
  coming_soon: string[];
  voice_minutes_limit: number | null;
  voice_minutes_used: number;
  voice_minutes_daily_limit: number | null;
  voice_minutes_used_today: number;
  max_courses: number | null;
  ai_credits_limit: number | null;
  ai_credits_used: number;
}

interface Envelope<T> {
  status: string;
  message: string;
  data: T;
}

class EntitlementService {
  async getMine(): Promise<Entitlements> {
    try {
      const response = await apiClient.get<Envelope<Entitlements>>('/api/v1/subscription/entitlements');
      return response.data.data;
    } catch (error) {
      handleAPIError(error);
    }
  }
}

export const entitlementService = new EntitlementService();
